import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useI18n } from "../lib/i18n";
import { useSeo } from "../lib/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AboutPage() {
  useSeo({
    title: "About Motravay — The job board for Mauritius",
    description: "Motravay is the next-generation job board for Mauritius — built locally, for the Mauritian market. Learn about our mission and team.",
    canonical: "/about",
  });
  return (
    <div className="max-w-3xl mx-auto px-4 py-16" data-testid="about-page">
      <h1 className="font-display text-4xl font-bold text-slate-900">About Motravay</h1>
      <p className="mt-4 text-slate-700 leading-relaxed">Motravay is the next-generation job board built for Mauritius. We connect candidates and employers with a fast, transparent, and modern hiring experience — from a chauffeur opening in Port-Louis to a developer role in Ebène.</p>
      <p className="mt-3 text-slate-700 leading-relaxed">Our platform combines a powerful job search, smart job descriptions, candidate pipelines for recruiters, and a curated career page for each employer.</p>
    </div>
  );
}

export function PricingPage() {
  const { lang } = useI18n();
  useSeo({
    title: lang === "fr" ? "Tarifs & devis pour recruteurs — Motravay" : "Pricing & quote request for employers — Motravay",
    description: lang === "fr"
      ? "Publiez vos offres d'emploi sur Motravay. Demandez un devis personnalisé selon vos besoins de recrutement à Maurice."
      : "Post your job openings on Motravay. Request a tailored quote based on your hiring needs in Mauritius.",
    canonical: "/pricing",
  });
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", vacancies: "", needs: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.contact_name.trim() || !form.email.trim() || !form.phone.trim() || !form.needs.trim()) {
      toast.error(lang === "fr" ? "Merci de remplir les champs obligatoires." : "Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/pricing-inquiry`, form);
      setSubmitted(true);
      toast.success(lang === "fr" ? "Merci ! Nous vous recontactons sous 24h." : "Thanks! We'll get back to you within 24h.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "fr" ? "Envoi impossible. Réessayez." : "Could not submit. Please retry."));
    } finally {
      setSubmitting(false);
    }
  };

  const tr = (en, fr) => (lang === "fr" ? fr : en);

  return (
    <div className="max-w-3xl mx-auto px-4 py-16" data-testid="pricing-page">
      <div className="text-center">
        <span className="text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold">{tr("Pricing", "Tarifs")}</span>
        <h1 className="font-display text-4xl font-bold text-slate-900 mt-2">{tr("Tell us about your hiring needs", "Parlez-nous de vos besoins de recrutement")}</h1>
        <p className="mt-3 text-slate-600 max-w-xl mx-auto">
          {tr(
            "Every business is different. Share a few details and our team will reach out with a tailored quote that matches your vacancies and timeline.",
            "Chaque entreprise est unique. Donnez-nous quelques infos et notre équipe vous enverra une offre adaptée à vos postes à pourvoir."
          )}
        </p>
      </div>

      {submitted ? (
        <Card className="p-8 mt-10 text-center" data-testid="pricing-inquiry-success">
          <h2 className="font-display text-2xl font-bold text-slate-900">{tr("Request received", "Demande bien reçue")}</h2>
          <p className="mt-2 text-slate-600">{tr("Our team will get back to you within 24 hours.", "Notre équipe vous recontacte sous 24 heures.")}</p>
        </Card>
      ) : (
        <Card className="p-6 md:p-8 mt-10">
          <form className="space-y-5" onSubmit={submit} data-testid="pricing-inquiry-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="pi-company">{tr("Company *", "Entreprise *")}</Label>
                <Input id="pi-company" data-testid="pi-company" value={form.company_name} onChange={update("company_name")} required />
              </div>
              <div>
                <Label htmlFor="pi-contact">{tr("Contact person *", "Personne de contact *")}</Label>
                <Input id="pi-contact" data-testid="pi-contact" value={form.contact_name} onChange={update("contact_name")} required />
              </div>
              <div>
                <Label htmlFor="pi-email">{tr("Email *", "Email *")}</Label>
                <Input id="pi-email" data-testid="pi-email" type="email" value={form.email} onChange={update("email")} required />
              </div>
              <div>
                <Label htmlFor="pi-phone">{tr("Phone *", "Téléphone *")}</Label>
                <Input id="pi-phone" data-testid="pi-phone" type="tel" value={form.phone} onChange={update("phone")} placeholder="+230 ..." required />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="pi-vacancies">{tr("Vacancies to post", "Nombre de postes à publier")}</Label>
                <Input id="pi-vacancies" data-testid="pi-vacancies" value={form.vacancies} onChange={update("vacancies")} placeholder={tr("e.g. 5 / month", "ex. 5 / mois")} />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="pi-needs">{tr("Tell us about your needs *", "Décrivez vos besoins *")}</Label>
                <Textarea id="pi-needs" data-testid="pi-needs" value={form.needs} onChange={update("needs")} rows={5} required placeholder={tr("Roles, locations, timeline, employer branding, anything we should know.", "Postes, lieux, délais, marque employeur, tout ce qu'il faut savoir.")} />
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full md:w-auto rounded-full bg-[#1A8FE3] hover:bg-[#157AC0] text-white px-8 py-2.5" data-testid="pi-submit">
              {submitting ? tr("Sending…", "Envoi…") : tr("Request a quote", "Demander un devis")}
            </Button>
          </form>
        </Card>
      )}
    </div>
  );
}

export function ContactPage() {
  const { lang } = useI18n();
  useSeo({
    title: lang === "fr" ? "Contact — Motravay" : "Contact us — Motravay",
    description: lang === "fr"
      ? "Contactez Motravay : info@motravay.mu, +230 5472 3295, St Jean Quatre Bornes, Maurice."
      : "Get in touch with Motravay: info@motravay.mu, +230 5472 3295, St Jean Quatre Bornes, Mauritius.",
    canonical: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Motravay",
      "url": "https://motravay.mu",
      "email": "info@motravay.mu",
      "telephone": "+230 5472 3295",
      "address": { "@type": "PostalAddress", "streetAddress": "St Jean", "addressLocality": "Quatre Bornes", "addressCountry": "MU" },
    },
  });
  const tr = (en, fr) => (lang === "fr" ? fr : en);
  return (
    <div className="max-w-2xl mx-auto px-4 py-16" data-testid="contact-page">
      <h1 className="font-display text-4xl font-bold text-slate-900">{tr("Contact us", "Nous contacter")}</h1>
      <p className="mt-4 text-slate-700">{tr("Get in touch with the Motravay team.", "Contactez l'équipe Motravay.")}</p>
      <Card className="p-6 mt-6 space-y-3 text-slate-700" data-testid="contact-details">
        <p data-testid="contact-email"><strong>{tr("Email:", "Email :")}</strong> <a className="text-[#1A8FE3] hover:underline" href="mailto:info@motravay.mu">info@motravay.mu</a></p>
        <p data-testid="contact-phone"><strong>{tr("Phone:", "Téléphone :")}</strong> <a className="text-[#1A8FE3] hover:underline" href="tel:+23054723295">+230 5472 3295</a></p>
        <p data-testid="contact-address"><strong>{tr("Address:", "Adresse :")}</strong> St Jean, Quatre Bornes, Mauritius</p>
      </Card>
    </div>
  );
}

export function TermsPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-16" data-testid="terms-page">
      <h1 className="font-display text-4xl font-bold text-slate-900">Terms of service</h1>
      <p className="mt-4 text-slate-700 leading-relaxed">By using Motravay, you agree to use the platform respectfully and not to post fraudulent or discriminatory content. Full terms will be published soon.</p>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-16" data-testid="privacy-page">
      <h1 className="font-display text-4xl font-bold text-slate-900">Privacy policy</h1>
      <p className="mt-4 text-slate-700 leading-relaxed">Motravay processes personal data (name, email, phone, CV, applications) strictly to operate the platform and connect candidates with employers. We never sell your data. Full policy will be published soon.</p>
    </div>
  );
}
